import * as React from 'react'
import { validateVatNumber } from './useChargebeeCheckout'
import { countryName } from './countries'

// The VAT number field. A number joins the order (`details.vatNumber`) only
// once Chargebee has confirmed it with VIES: the quote prices any number as a
// reverse charge, while the customer record refuses an invalid one, so an
// unconfirmed number would show a price the charge cannot match. A number VIES
// cannot confirm right now stays out of the order: the buyer pays VAT and can
// check again.
export function useVatField({ config, details, setDetails }) {
    const [draft, setDraft] = React.useState(details.vatNumber)
    // The number the buyer last committed (on blur / Enter), and the answer
    // for exactly that number and country.
    const [committed, setCommitted] = React.useState(details.vatNumber)
    const [check, setCheck] = React.useState({
        status: 'idle',
        number: '',
        country: '',
    })
    const sequenceRef = React.useRef(0)
    const applies = Boolean(
        config &&
            config.vatCountries &&
            config.vatCountries.includes(details.country)
    )

    const adopt = React.useCallback(
        vatNumber =>
            setDetails(current =>
                current.vatNumber === vatNumber
                    ? current
                    : { ...current, vatNumber }
            ),
        [setDetails]
    )

    const verify = React.useCallback(
        async (number, country) => {
            const sequence = ++sequenceRef.current
            setCheck({ status: 'checking', number, country })
            let result
            try {
                result = await validateVatNumber({
                    site: config.site,
                    publishableKey: config.publishableKey,
                    country,
                    vatNumber: number,
                })
            } catch (error) {
                result = 'UNDETERMINED'
            }
            if (sequence !== sequenceRef.current) return
            const status =
                result === 'VALID'
                    ? 'valid'
                    : result === 'INVALID'
                    ? 'invalid'
                    : 'unverified'
            setCheck({ status, number, country })
            adopt(status === 'valid' ? number : '')
        },
        [config, adopt]
    )

    // A new number, or a new country for the same number, is checked afresh.
    React.useEffect(() => {
        if (!committed || !applies) {
            sequenceRef.current += 1
            setCheck({ status: 'idle', number: '', country: '' })
            adopt('')
            return
        }
        verify(committed, details.country)
    }, [committed, applies, details.country, verify, adopt])

    const current =
        check.number === committed && check.country === details.country
    const status = applies && committed && current ? check.status : 'idle'

    // Chargebee refused the number when the customer took it (the browser
    // check said otherwise, or was skipped): the order goes back to VAT.
    const refuse = React.useCallback(() => {
        sequenceRef.current += 1
        setCheck({
            status: 'invalid',
            number: committed,
            country: details.country,
        })
        adopt('')
    }, [committed, details.country, adopt])

    return {
        draft,
        change: setDraft,
        commit: () => setCommitted(draft.trim()),
        recheck: () => verify(committed, details.country),
        applies,
        status,
        error:
            status === 'invalid'
                ? `That VAT number isn't valid for ${countryName(
                      details.country
                  )}. Check it, or leave the field empty.`
                : null,
        // Paying is held while the number is checked or refused; an
        // unverified number is simply left out of the order.
        blocksPayment:
            applies &&
            Boolean(committed) &&
            status !== 'valid' &&
            status !== 'unverified',
        refuse,
    }
}
